/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { ComponentChildren, createContext, h, VNode } from "preact";
import { useContext, useEffect, useState } from "preact/hooks";
import {
  AppLocation,
  ObjectOf,
  Location,
  findMatch,
  RouteDefinition,
  LocationNotFound,
} from "../utils/route.js";

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

type Type = {
  path: string;
  params: Record<string, string[]>;
  navigateTo: (path: AppLocation) => void;
  // addNavigationListener: (listener: (path: string, params: Record<string, string>) => void) => (() => void);
};

const Context = createContext<Type | undefined>(undefined);

export const useNavigationContext = (): Type => useContext(Context)!;

export const useOptionalNavigationContext = (): Type | undefined =>
  useContext(Context);

export function useCurrentLocation<T extends ObjectOf<RouteDefinition<any>>>(
  pagesMap: T,
): Location<T> | LocationNotFound<T> {
  const pageList = Object.keys(pagesMap as object) as Array<keyof T>;
  const { path, params } = useNavigationContext();

  return findMatch(pagesMap, pageList, path, params);
}

export function parseHashLocation(
  hash: string,
  search: string,
): {
  path: string;
  params: Record<string, string[]>;
} {
  const rawHash = hash.startsWith("#") ? hash.substring(1) : hash;
  const queryIndex = rawHash.indexOf("?");
  const path = queryIndex < 0 ? rawHash : rawHash.substring(0, queryIndex);
  const params: Record<string, string[]> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (!params[key]) {
      params[key] = [];
    }
    params[key].push(value);
  }
  if (queryIndex >= 0) {
    for (const [key, value] of new URLSearchParams(
      rawHash.substring(queryIndex + 1),
    )) {
      if (!params[key]) {
        params[key] = [];
      }
      params[key].push(value);
    }
  }
  return { path, params };
}

function getPathAndParamsFromWindow(): {
  path: string;
  params: Record<string, string[]>;
} {
  if (typeof window === "undefined") {
    return parseHashLocation("/", "");
  }
  return parseHashLocation(window.location.hash, window.location.search);
}

const { path: initialPath, params: initialParams } =
  getPathAndParamsFromWindow();

// there is a possibility that if the browser does a redirection
// (which doesn't go through navigatTo function) and that executed
// too early (before addEventListener runs) it won't be taking
// into account
const PopStateEventType = "popstate";

export const BrowserHashNavigationProvider = ({
  children,
}: {
  children: ComponentChildren;
}): VNode<any> => {
  const [{ path, params }, setState] = useState({
    path: initialPath,
    params: initialParams,
  });
  if (typeof window === "undefined") {
    throw Error(
      "Can't use BrowserHashNavigationProvider if there is no window object",
    );
  }
  function navigateTo(path: string): void {
    const target = new URL(path, window.location.href);
    setState(parseHashLocation(target.hash, target.search));
    window.location.href = target.href;
  }

  useEffect(() => {
    function eventListener(): void {
      setState(getPathAndParamsFromWindow());
    }
    window.addEventListener(PopStateEventType, eventListener);
    window.addEventListener("hashchange", eventListener);
    return () => {
      window.removeEventListener(PopStateEventType, eventListener);
      window.removeEventListener("hashchange", eventListener);
    };
  }, []);
  return h(Context.Provider, {
    value: { path, params, navigateTo },
    children,
  });
};
